import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  AlertCircle,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useRejectOvertimeRequestMutation,
  useForwardOvertimeRequestMutation,
  useRequestReapplyMutation,
  useGetSupervisorDivisionRequestsQuery,
} from "../../slices/overtimeApiSlice";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function SupervisorOvertimeRequests() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Selected record for detail modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [supervisorNote, setSupervisorNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Status filter
  const [status, setStatus] = useState("All");
  // Date range state (default to last 1 week)
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };

  const today = getTodayInThimphu();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(today.getDate() - 6); // 7 days including today

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDateString(oneWeekAgo));
  const [endDate, setEndDate] = useState(formatDateString(today));
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Helper function to format date for display
  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (short) {
      // dd/mm/yyyy format for compact display
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Status options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "PENDING", label: "Pending" },
    { value: "REAPPLY", label: "Re-Apply" },
    { value: "REAPPLIED", label: "Re-Applied" },
    { value: "REJECTED", label: "Rejected" },
    { value: "FORWARDED", label: "Forwarded" },
    { value: "APPROVED", label: "Approved" },
  ];

  // Fetch overtime requests from API - Division-based for supervisors (only their division's technicians)
  const { data, isLoading, isError, error, refetch } =
    useGetSupervisorDivisionRequestsQuery();

  // Supervisor action mutations
  const [rejectRequest] = useRejectOvertimeRequestMutation();
  const [forwardRequest] = useForwardOvertimeRequestMutation();
  const [requestReapplyMutation] = useRequestReapplyMutation();

  // Helper to format backend ISO date string
  function formatBackendDate(iso) {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} - ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })}`;
  }

  // Format note timestamp
  function formatNoteTimestamp(timestamp) {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      // Add 6 hours to adjust timezone (same as Submitted On logic)
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `Note added on: ${dateStr}, ${timeStr}`;
    } catch (error) {
      return "";
    }
  }

  // Format hours - show minutes if not a whole hour, otherwise show hours and minutes
  function formatHours(totalHours) {
    if (!totalHours || totalHours === 0) return "0 hrs";

    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} hr${hours !== 1 ? "s" : ""}`;
    } else {
      return `${hours} hr${hours !== 1 ? "s" : ""} ${minutes} min`;
    }
  }

  // Format date for submission timestamp
  function formatSubmittedDate(dateString) {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  }

  // Format date and time for submission timestamp
  function formatSubmittedDateTime(dateString) {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return dateString;
    }
  }

  // Map backend data to table rows
  const requests = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const statusColorMap = {
      APPROVED: "bg-blue-100 text-blue-700",
      Approved: "bg-blue-100 text-blue-700",
      REJECTED: "bg-red-100 text-red-700",
      Rejected: "bg-red-100 text-red-700",
      PENDING: "bg-yellow-100 text-yellow-700",
      Pending: "bg-yellow-100 text-yellow-700",
      FORWARDED: "bg-gray-100 text-gray-700",
      Forwarded: "bg-gray-100 text-gray-700",
      REAPPLY: "bg-orange-100 text-orange-700",
      "Re-Apply": "bg-orange-100 text-orange-700",
      REAPPLIED: "bg-orange-100 text-orange-700",
      "Re-Applied": "bg-orange-100 text-orange-700",
    };

    const statusDisplay = {
      PENDING: "Pending",
      REAPPLY: "Re-Apply",
      REAPPLIED: "Re-Applied",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      APPROVED: "Approved",
    };

    return data
      .filter((item) => item && (item.details || item.startTime)) // Filter out null items
      .map((item) => {
        const displayStatus =
          statusDisplay[item.status] || item.status || "Pending";

        // Handle both new structure (with details object) and fallback structure
        const details = item.details || item;

        // Extract supervisor note and supervisor email from notes array
        let supervisorNote = "";
        let supervisorEmail = "";
        let supervisorTimestamp = "";
        let managerNote = "";
        let managerEmail = "";
        let managerTimestamp = "";

        if (
          details.notes &&
          Array.isArray(details.notes) &&
          details.notes.length > 0
        ) {
          // Go through notes to find supervisor and manager notes
          // Use separate tracking to ensure we get LAST of each type
          let lastSupervisorNote = null;
          let lastManagerNote = null;

          details.notes.forEach((note) => {
            // Check action field - this is the most reliable indicator
            if (note.action === "REJECTED") {
              // Manager rejection - uses the supervisor field to store manager email
              lastManagerNote = note;
            } else if (note.action === "FORWARDED") {
              // Supervisor forward
              lastSupervisorNote = note;
            }
            // Fallback: check recordedBy field if action isn't present
            else if (note.recordedBy === "MANAGER") {
              lastManagerNote = note;
            } else if (note.recordedBy === "SUPERVISOR") {
              lastSupervisorNote = note;
            }
            // Fallback: check email fields
            else if (note.managerEmail) {
              lastManagerNote = note;
            } else if (note.supervisorEmail) {
              lastSupervisorNote = note;
            }
          });

          // Extract supervisor data (from FORWARDED action)
          if (lastSupervisorNote) {
            supervisorNote =
              lastSupervisorNote.note || lastSupervisorNote.reason || "";
            // Supervisor email can be in supervisorEmail or supervisor field
            supervisorEmail = (
              lastSupervisorNote.supervisorEmail ||
              lastSupervisorNote.supervisor ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com"); // Fix double @company.com
            supervisorTimestamp =
              lastSupervisorNote.createdAt ||
              lastSupervisorNote.recordedAt ||
              lastSupervisorNote.timestamp ||
              "";
          }

          // Extract manager data (from REJECTED action)
          if (lastManagerNote) {
            managerNote = lastManagerNote.note || lastManagerNote.reason || "";
            // For REJECTED action, manager email is in the supervisor field
            managerEmail = (
              lastManagerNote.supervisorEmail ||
              lastManagerNote.supervisor ||
              lastManagerNote.managerEmail ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com"); // Fix double @company.com
            managerTimestamp =
              lastManagerNote.createdAt ||
              lastManagerNote.recordedAt ||
              lastManagerNote.timestamp ||
              "";
          }
        }

        return {
          id: details.id || item.overtimeId || item.id,
          employee:
            details.userName || item.employeeName || item.userId || "N/A",
          email: details.userEmail || item.email || item.employeeEmail || "N/A",
          from: formatBackendDate(details.startTime),
          to: formatBackendDate(details.endTime),
          description: details.description || "No description",
          hours: formatHours(details.totalHours || 0),
          status: displayStatus,
          statusColor:
            statusColorMap[item.status] || "bg-gray-100 text-gray-700",
          supervisorNote: supervisorNote,
          supervisorEmail: supervisorEmail,
          supervisorTimestamp: supervisorTimestamp,
          managerNote: managerNote,
          managerEmail: managerEmail,
          managerTimestamp: managerTimestamp,
          submissionDate:
            details.submittedAt ||
            details.createdAt ||
            details.recordedDate ||
            new Date().toISOString(),
        };
      });
  }, [data]);

  // Filtered records based on status and date range
  const filteredRecords = useMemo(() => {
    const backendStatusMap = {
      Pending: "PENDING",
      "Re-Apply": "REAPPLY",
      "Re-Applied": "REAPPLIED",
      Rejected: "REJECTED",
      Forwarded: "FORWARDED",
      Approved: "APPROVED",
    };

    let filtered = requests.filter((item) => {
      const itemBackendStatus = backendStatusMap[item.status] || item.status;
      if (status !== "All" && itemBackendStatus !== status) return false;
      return true;
    });

    // Apply date range filter only if both dates are selected
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        try {
          // Check date range - parse "Oct 1, 2025" format from item.from
          const dateStr = item.from?.split(" - ")[0];
          if (!dateStr) return false;

          const itemDate = new Date(dateStr);
          if (isNaN(itemDate.getTime())) return false; // Invalid date

          itemDate.setHours(0, 0, 0, 0);
          const filterStart = new Date(startDate);
          filterStart.setHours(0, 0, 0, 0);
          const filterEnd = new Date(endDate);
          filterEnd.setHours(23, 59, 59, 999);

          const isInRange = itemDate >= filterStart && itemDate <= filterEnd;
          return isInRange;
        } catch (err) {
          console.error("Date filtering error:", err, item);
          return false;
        }
      });
    }

    // Sort by latest submission date first (most recent requests first)
    // Use stable sort with ID as tiebreaker to ensure consistent ordering
    return filtered.sort((a, b) => {
      const dateA = new Date(a.submissionDate);
      const dateB = new Date(b.submissionDate);
      const timeDiff = dateB - dateA;
      // If timestamps are equal, use ID comparison for stability
      if (timeDiff === 0) {
        return (b.id || "").toString().localeCompare((a.id || "").toString());
      }
      return timeDiff;
    });
  }, [requests, status, startDate, endDate]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const totalResults = filteredRecords.length;
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Action Handlers
  const handleReject = async () => {
    // Validation
    const reason = supervisorNote.trim();
    if (!reason) {
      setActionError("Please add a reject reason");
      return;
    }

    if (!selectedRecord?.id) {
      setActionError("Request ID is missing");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setActionMessage(null);

    try {
      // Call API with proper parameters
      const response = await rejectRequest({
        requestId: selectedRecord.id,
        rejectReason: reason,
      }).unwrap();

      // Success handling
      setActionMessage("Request rejected successfully");

      // Auto-close and refresh after delay
      setTimeout(() => {
        setSelectedRecord(null);
        setSupervisorNote("");
        setActionMessage(null);
        refetch(); // Refresh the overtime list
      }, 1500);
    } catch (err) {
      // Error handling
      const errorMessage =
        err?.data?.error || err?.message || "Failed to reject request";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleForward = async () => {
    // Validation
    const note = supervisorNote.trim();
    if (!note) {
      setActionError("Please add a note before forwarding");
      return;
    }

    if (!selectedRecord?.id) {
      setActionError("Request ID is missing");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setActionMessage(null);

    try {
      // Call API with proper parameters
      const response = await forwardRequest({
        requestId: selectedRecord.id,
        supervisorNote: note,
      }).unwrap();

      // Success handling
      setActionMessage("Request forwarded successfully to manager");

      // Auto-close and refresh after delay
      setTimeout(() => {
        setSelectedRecord(null);
        setSupervisorNote("");
        setActionMessage(null);
        refetch(); // Refresh the overtime list
      }, 1500);
    } catch (err) {
      // Error handling
      const errorMessage =
        err?.data?.error || err?.message || "Failed to forward request";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestReapply = async () => {
    if (!supervisorNote.trim()) {
      setActionError("Please add a note before requesting reapply");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setActionMessage(null);

    try {
      await requestReapplyMutation({
        requestId: selectedRecord.id,
        reapplyReason: supervisorNote,
      }).unwrap();

      setActionMessage("Reapply request sent to technician");
      setTimeout(() => {
        setSelectedRecord(null);
        setSupervisorNote("");
        setActionMessage(null);
        refetch();
      }, 1500);
    } catch (err) {
      setActionError(err?.data?.error || "Failed to request reapply");
    } finally {
      setActionLoading(false);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-1 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition"
          title="Previous page"
        >
          <ChevronLeft size={16} />
        </button>,
      );
    }

    // First page button
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 transition"
        >
          1
        </button>,
      );

      // Ellipsis if there's a gap
      if (startPage > 2) {
        pages.push(
          <span
            key="ellipsis1"
            className="w-8 h-8 flex items-center justify-center text-gray-600"
          >
            ...
          </span>,
        );
      }
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`w-8 h-8 flex items-center justify-center rounded transition ${
            currentPage === i
              ? "bg-blue-500 text-white font-semibold"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {i}
        </button>,
      );
    }

    // Last page button
    if (endPage < totalPages) {
      // Ellipsis if there's a gap
      if (endPage < totalPages - 1) {
        pages.push(
          <span
            key="ellipsis2"
            className="w-8 h-8 flex items-center justify-center text-gray-600"
          >
            ...
          </span>,
        );
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 transition"
        >
          {totalPages}
        </button>,
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-1 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition"
          title="Next page"
        >
          <ChevronRight size={16} />
        </button>,
      );
    }

    return pages;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with chevron */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Overtime Requests
          </h1>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">Records</h2>
              <p className="text-sm text-zinc-600 mt-1">
                All overtime requests from your division
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <CustomSelect
                options={statusOptions}
                value={status}
                onChange={(value) => {
                  setStatus(value);
                  setCurrentPage(1);
                }}
                placeholder="All"
                minWidth={150}
              />
              <div className="relative">
                <button
                  ref={dateRangeButtonRef}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
                >
                  {startDate && endDate ? (
                    <>
                      <span className="hidden lg:inline">
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </span>
                      <span className="lg:hidden">
                        {formatDate(startDate, true)} -{" "}
                        {formatDate(endDate, true)}
                      </span>
                    </>
                  ) : (
                    "Select date range"
                  )}
                </button>
                <DateRangePicker
                  show={showDateRangePicker}
                  onClose={() => setShowDateRangePicker(false)}
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={({ start, end }) => {
                    if (start && end) {
                      setStartDate(start);
                      setEndDate(end);
                    }
                  }}
                  buttonRef={dateRangeButtonRef}
                  allowFutureDates={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="overflow-x-auto min-w-0">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                Loading overtime requests...
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <p className="text-red-600 font-semibold">
                  Failed to load requests
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  {error?.status && `Error: ${error.status}`}
                  {error?.data?.message && ` - ${error.data.message}`}
                </p>
              </div>
            ) : paginatedRecords.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                {requests.length === 0
                  ? "No overtime requests"
                  : "No overtime requests found for the selected filters"}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-zinc-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      From Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      To Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-8 text-center text-zinc-500"
                      >
                        Loading records...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-8 text-center text-red-500"
                      >
                        Error loading records:{" "}
                        {error?.data?.message ||
                          error?.message ||
                          "Please try again."}
                      </td>
                    </tr>
                  ) : paginatedRecords.length > 0 ? (
                    paginatedRecords.map((item, index) => (
                      <tr
                        key={item.id || index}
                        className="hover:bg-zinc-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedRecord(item)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="font-medium text-zinc-900">
                            {item.employee}
                          </div>
                          <div className="text-zinc-600 text-xs">
                            {item.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                          {item.from.split(" - ")[0]}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.from.split(" - ")[1]
                            ? `${item.from
                                .split(" - ")[1]
                                .substring(0, 5)} ${item.from
                                .split(" - ")[1]
                                .substring(9, 11)}`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.to.split(" - ")[1]
                            ? `${item.to
                                .split(" - ")[1]
                                .substring(0, 5)} ${item.to
                                .split(" - ")[1]
                                .substring(9, 11)}`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.hours}
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          <div
                            className="truncate max-w-xs"
                            title={item.description}
                          >
                            {item.description.length > 35
                              ? `${item.description.substring(0, 35)}...`
                              : item.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${item.statusColor}`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-8 text-center text-zinc-500"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-zinc-900 text-white"
                          : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 7 && currentPage < totalPages - 3 && (
                  <>
                    <span className="px-2 text-zinc-400">...</span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-1 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() =>
                  handlePageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-zinc-600 font-medium">
              {totalResults} Results
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setSelectedRecord(null);
              setSupervisorNote("");
            }}
            style={{ zIndex: 9998 }}
          />
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header with Status Badge and Close */}
              <div className="flex items-center justify-between px-6 pt-4">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${selectedRecord.statusColor}`}
                >
                  {selectedRecord.status}
                </span>
                <button
                  onClick={() => {
                    setSelectedRecord(null);
                    setSupervisorNote("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Avatar and Title - Redesigned */}
              <div className="px-6 py-6 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-2xl">
                      {selectedRecord.userName?.charAt(0).toUpperCase() ||
                        selectedRecord.employee?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedRecord.userName || selectedRecord.employee}
                    </h2>
                    <p className="text-lg font-medium text-gray-700">
                      {selectedRecord.userEmail || selectedRecord.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-0">
                {/* Description Row */}
                <div className="py-4 border-b border-gray-200">
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded mb-2">
                    Description
                  </span>
                  <p className="text-gray-700 text-sm">
                    {selectedRecord.description}
                  </p>
                </div>

                {/* Date Row */}
                <div className="py-4 border-b border-gray-200 flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-600">
                    Date
                  </label>
                  <p className="text-gray-900 font-semibold text-right">
                    {selectedRecord.from.split(" - ")[0]}
                  </p>
                </div>

                {/* From Row */}
                <div className="py-4 border-b border-gray-200 flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-600">
                    From:
                  </label>
                  <p className="text-gray-900 font-semibold text-right">
                    {selectedRecord.from.split(" - ")[1]}
                  </p>
                </div>

                {/* To Row */}
                <div className="py-4 border-b border-gray-200 flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-600">
                    To
                  </label>
                  <p className="text-gray-900 font-semibold text-right">
                    {selectedRecord.to.split(" - ")[1]}
                  </p>
                </div>

                {/* Hours Row */}
                <div className="py-4 border-b border-gray-200 flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-600">
                    Hours
                  </label>
                  <p className="text-gray-400 text-sm text-right">
                    {selectedRecord.hours}
                  </p>
                </div>

                {/* Submitted On Row */}
                <div className="py-4 border-b border-gray-200 flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-600">
                    Submitted On:
                  </label>
                  <p className="text-gray-600 text-sm text-right">
                    {formatSubmittedDateTime(selectedRecord.submissionDate)}
                  </p>
                </div>

                {/* Supervisor Note Display - Show for Rejected status */}
                {selectedRecord.status === "Rejected" &&
                  selectedRecord.supervisorNote && (
                    <div className="py-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        {/* Supervisor Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-200">
                            <span className="font-semibold text-xs sm:text-sm text-blue-700">
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase() || "S"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Supervisor
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.supervisorEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-blue-700">
                            Forwarded Reason:
                          </p>
                          <p className="text-sm sm:text-base text-blue-900">
                            {selectedRecord.supervisorNote}
                          </p>
                          {selectedRecord.supervisorTimestamp && (
                            <p className="text-xs text-gray-500 mt-2">
                              {formatNoteTimestamp(
                                selectedRecord.supervisorTimestamp,
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {selectedRecord.status === "Forwarded" &&
                  selectedRecord.supervisorNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        {/* Supervisor Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-semibold text-xs sm:text-sm">
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase() || "S"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Supervisor
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.supervisorEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-blue-700">
                            Supervisor's Note:
                          </p>
                          <p className="text-sm sm:text-base text-blue-900">
                            {selectedRecord.supervisorNote}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {selectedRecord.status === "Approved" &&
                  selectedRecord.supervisorNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                        {/* Supervisor Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-semibold text-xs sm:text-sm">
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase() || "S"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Supervisor
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.supervisorEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-blue-700">
                            Supervisor's Note:
                          </p>
                          <p className="text-sm sm:text-base text-blue-900">
                            {selectedRecord.supervisorNote}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {selectedRecord.status === "Re-Apply" &&
                  selectedRecord.supervisorNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                        {/* Supervisor Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-orange-700 font-semibold text-xs sm:text-sm">
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase() || "S"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Supervisor
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.supervisorEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-orange-700">
                            Supervisor's Request:
                          </p>
                          <p className="text-sm sm:text-base text-orange-900">
                            {selectedRecord.supervisorNote}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Manager Note Display - Show for Approved/Rejected status */}
                {(selectedRecord.status === "Approved" ||
                  selectedRecord.status === "Rejected") &&
                  selectedRecord.managerNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div
                        className={`rounded-lg p-3 sm:p-4 ${
                          selectedRecord.status === "Approved"
                            ? "bg-green-50 border border-green-200"
                            : "bg-red-50 border border-red-200"
                        }`}
                      >
                        {/* Check if email contains supervisor role */}
                        {(() => {
                          const isSupervisorRole = selectedRecord.managerEmail
                            ?.toLowerCase()
                            .includes("supervisor");
                          return (
                            <>
                              {/* Manager/Supervisor Info */}
                              <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                                <div
                                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    selectedRecord.status === "Approved"
                                      ? "bg-green-200"
                                      : "bg-red-200"
                                  }`}
                                >
                                  <span
                                    className={`font-semibold text-xs sm:text-sm ${
                                      selectedRecord.status === "Approved"
                                        ? "text-green-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    {selectedRecord.managerEmail
                                      ?.charAt(0)
                                      .toUpperCase() ||
                                      (isSupervisorRole ? "S" : "M")}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                    {isSupervisorRole
                                      ? "Supervisor"
                                      : "Manager"}
                                  </h4>
                                  <p className="text-xs text-gray-600">
                                    {selectedRecord.managerEmail}
                                  </p>
                                </div>
                              </div>

                              {/* Note Content */}
                              <div className="ml-0 sm:ml-11">
                                <p
                                  className={`text-xs sm:text-sm font-medium mb-1 ${
                                    selectedRecord.status === "Approved"
                                      ? "text-green-700"
                                      : "text-red-700"
                                  }`}
                                >
                                  {selectedRecord.status === "Approved"
                                    ? isSupervisorRole
                                      ? "Supervisor's Approval Note:"
                                      : "Manager's Approval Note:"
                                    : isSupervisorRole
                                      ? "Supervisor Rejection Note:"
                                      : "Manager's Rejection Note:"}
                                </p>
                                <p
                                  className={`text-sm sm:text-base ${
                                    selectedRecord.status === "Approved"
                                      ? "text-green-900"
                                      : "text-red-900"
                                  }`}
                                >
                                  {selectedRecord.managerNote}
                                </p>
                                {selectedRecord.managerTimestamp && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    {formatNoteTimestamp(
                                      selectedRecord.managerTimestamp,
                                    )}
                                  </p>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                {/* Supervisor Note Input - Show for Pending and Re-Applied status */}
                {(selectedRecord.status === "Pending" ||
                  selectedRecord.status === "Re-Applied") && (
                  <div className="py-4">
                    <label className="text-sm font-medium text-gray-600 block mb-2">
                      Supervisor Note
                    </label>
                    <textarea
                      placeholder="Add note (required for all actions)"
                      value={supervisorNote}
                      onChange={(e) => setSupervisorNote(e.target.value)}
                      maxLength={500}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {supervisorNote.length}/500
                    </p>
                  </div>
                )}

                {/* Error/Success Messages */}
                {actionError && (
                  <div className="py-4 px-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle
                      size={18}
                      className="text-red-600 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-red-700">{actionError}</p>
                  </div>
                )}
                {actionMessage && (
                  <div className="py-4 px-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <CheckCircle
                      size={18}
                      className="text-green-600 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-green-700">{actionMessage}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons - Show for Pending and Re-Applied status */}
              {(selectedRecord.status === "Pending" ||
                selectedRecord.status === "Re-Applied") && (
                <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-center flex-wrap">
                  <button
                    onClick={() => handleRequestReapply()}
                    disabled={!supervisorNote.trim() || actionLoading}
                    className="px-6 py-2 bg-black text-white rounded-full font-medium text-sm hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? "Loading..." : "Request Reapply"}
                  </button>
                  <button
                    onClick={() => handleForward()}
                    disabled={!supervisorNote.trim() || actionLoading}
                    className="px-6 py-2 bg-blue-500 text-white rounded-full font-medium text-sm hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? "Loading..." : "Forward"}
                  </button>
                  <button
                    onClick={() => handleReject()}
                    disabled={!supervisorNote.trim() || actionLoading}
                    className="px-6 py-2 bg-red-500 text-white rounded-full font-medium text-sm hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? "Loading..." : "Reject"}
                  </button>
                </div>
              )}

              {/* Status Message - Show only for Pending and other non-final statuses */}
              {selectedRecord.status === "Pending" && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-center gap-2 text-gray-600">
                  <div className="w-4 h-4 rounded-full border-2 border-gray-400 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                  </div>
                  <span className="text-sm">Currently in review</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
